import Model, { attr, belongsTo, hasMany } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import type SchoolSubscription from 'teacher-ui-client/models/school-subscription'
import type SubscriptionCoordinator from 'teacher-ui-client/models/subscription-coordinator'
import type District from 'teacher-ui-client/models/district'
import type SchoolPartner from 'teacher-ui-client/models/school-partner'
import type Teacher from 'teacher-ui-client/models/teacher'
import type Accent from 'teacher-ui-client/models/accent'
import type Student from 'teacher-ui-client/models/student'
import type StandardsSet from 'teacher-ui-client/models/standards-set'
import type StringTransform from 'teacher-ui-client/transforms/string'
import type BooleanTransform from 'teacher-ui-client/transforms/boolean'
import type DateTransform from 'teacher-ui-client/transforms/date'
import type { ExternalRosteringSource } from 'teacher-ui-client/services/external-rostering'

export default class School extends Model {
  @attr<StringTransform>('string') declare name: string

  @attr<StringTransform>('string') declare schoolCode: string

  // Geographical state of school, eg. "California"
  @attr<StringTransform>('string') declare state: string

  /**
   * AU, GB, US, ... ISO_3166-1 alpha-2 codes https://en.wikipedia.org/wiki/ISO_3166-1
   */
  @attr<StringTransform>('string') declare countryCode: string

  /** users log in with via a 3rd party SSO product. Don't show logins, passwords or allow them to be edited */
  @attr<BooleanTransform>('boolean') declare usesExternalAuthentication: boolean

  /** users are created via a 3rd party system. Don't allow users to be added/uploaded (eg forms or csv upload) */
  @attr<BooleanTransform>('boolean') declare usesExternalAccountProvisioning: boolean

  /** records are managed via a 3rd party rostering product. Don't allow grades, classes to be edited */
  @attr<BooleanTransform>('boolean') declare usesExternalRostering: boolean

  @attr<StringTransform>('string') declare externalRosteringSource?: Nullable<ExternalRosteringSource>

  @attr<StringTransform>('string') declare oneRosterEnabledAt?: Nullable<string>

  @attr<BooleanTransform>('boolean') declare isHomeschool: boolean

  @attr<DateTransform>('date') declare updatedAt?: Date

  @belongsTo<Accent>('accent', { async: false, inverse: null }) declare accent: Accent

  @hasMany<Teacher>('teacher', { async: false, inverse: 'school' }) declare teachers?: Teacher[]

  @hasMany<Student>('student', { async: false, inverse: 'school' }) declare students?: Student[]

  @hasMany<SchoolSubscription>('school-subscription', { async: false, inverse: 'school' })
  declare schoolSubscriptions: SchoolSubscription[]

  @hasMany<SubscriptionCoordinator>('subscription-coordinator', { async: false, inverse: 'school' })
  declare subscriptionCoordinators?: SubscriptionCoordinator[]

  @belongsTo<District>('district', { async: false, inverse: null }) declare district?: District

  @belongsTo<StandardsSet>('standards-set', { async: false, inverse: null })
  declare standardsSet?: StandardsSet

  @hasMany<SchoolPartner>('school-partner', { async: false, inverse: 'school' })
  declare schoolPartners?: SchoolPartner[];

  [Type] = 'school' as const
}
